import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Notebook,
  Glasses,
  Award,
  Star,
  Crown,
  Skull,
  Flame,
  MessageSquare,
  Heart,
  Calendar,
  Coins,
  Zap,
  TrendingUp,
  ArrowLeft,
  X,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

/**
 * RankPreviewPage — Static visual mockup of the "Detective Rank" gamification system.
 * Pure UX preview: no backend logic, no persistence. Mocked data so the owner can
 * decide if they like the experience before we build the real thing.
 */

const RANKS = [
  {
    id: 'rookie',
    name: 'Rookie',
    icon: Search,
    minXp: 0,
    color: 'text-gray-400',
    bg: 'bg-gray-700',
    border: 'border-gray-500',
    glow: 'shadow-gray-500/30',
    tagline: 'Just got your badge. Welcome to the precinct.',
    perks: ['Access to public case files', 'Comment on cases'],
  },
  {
    id: 'investigator',
    name: 'Investigator',
    icon: Notebook,
    minXp: 500,
    color: 'text-amber-700',
    bg: 'bg-amber-900',
    border: 'border-amber-700',
    glow: 'shadow-amber-700/40',
    tagline: 'You\'ve taken the case. Notebook in hand.',
    perks: ['Custom chat name color', 'Profile flair'],
  },
  {
    id: 'detective',
    name: 'Detective',
    icon: Glasses,
    minXp: 1500,
    color: 'text-slate-300',
    bg: 'bg-slate-600',
    border: 'border-slate-400',
    glow: 'shadow-slate-400/40',
    tagline: 'Seasoned. You can read between the lines.',
    perks: ['Sneak peek of upcoming cases', 'Detective-only chat room'],
  },
  {
    id: 'inspector',
    name: 'Inspector',
    icon: Award,
    minXp: 3500,
    color: 'text-yellow-400',
    bg: 'bg-yellow-700',
    border: 'border-yellow-500',
    glow: 'shadow-yellow-500/50',
    tagline: 'Respected. The brass knows your name.',
    perks: ['24h early access to new cases', 'Monthly Q&A access'],
  },
  {
    id: 'lieutenant',
    name: 'Lieutenant',
    icon: Star,
    minXp: 7500,
    color: 'text-amber-400',
    bg: 'bg-amber-700',
    border: 'border-amber-400',
    glow: 'shadow-amber-400/60',
    tagline: 'You lead the squad. Veterans look to you.',
    perks: ['Vote on future case topics', '15% store discount'],
  },
  {
    id: 'captain',
    name: 'Captain',
    icon: Crown,
    minXp: 15000,
    color: 'text-red-400',
    bg: 'bg-red-900',
    border: 'border-red-500',
    glow: 'shadow-red-500/60',
    tagline: 'You\'ve seen it all. The precinct runs on your watch.',
    perks: ['Name in case credits', '25% store discount', 'Exclusive merch drop'],
  },
  {
    id: 'chief',
    name: 'Chief',
    icon: Skull,
    minXp: 30000,
    color: 'text-red-500',
    bg: 'bg-black',
    border: 'border-red-600',
    glow: 'shadow-red-600/70',
    tagline: 'Legend. The case files have your fingerprints.',
    perks: ['Monthly 1-on-1 DM with Daria', 'Lifetime store discount', 'Annual signed memorabilia'],
  },
];

const XP_SOURCES = [
  { label: 'Daily check-in', amount: '+10 XP', icon: Calendar },
  { label: 'Read a case file', amount: '+5 XP', icon: Search },
  { label: 'Leave a comment', amount: '+15 XP', icon: MessageSquare },
  { label: 'React to a case', amount: '+2 XP', icon: Heart },
  { label: 'Tip the detective', amount: '+50 XP / $1', icon: Coins },
  { label: 'Active subscription month', amount: '+250 XP', icon: TrendingUp },
  { label: '7-day login streak', amount: '+100 XP bonus', icon: Flame },
];

// Mock current user
const SAMPLE_USER = {
  name: 'Detective Morrison',
  currentXp: 2150,
  recentActivity: [
    { what: 'Subscription month bonus', xp: 250, when: '2 days ago' },
    { what: 'Commented on "The Velvet Lounge Murders"', xp: 15, when: '3 days ago' },
    { what: '7-day streak bonus', xp: 100, when: '4 days ago' },
    { what: 'Tipped Daria $5', xp: 250, when: '6 days ago' },
  ],
};

const getCurrentRank = (xp) => {
  let current = RANKS[0];
  for (const r of RANKS) {
    if (xp >= r.minXp) current = r;
  }
  return current;
};

const getNextRank = (xp) => {
  return RANKS.find((r) => r.minXp > xp) || null;
};

const RankBadge = ({ rank, size = 'md', showName = true, animate = false }) => {
  const Icon = rank.icon;
  const sizes = {
    sm: { box: 'w-7 h-7', icon: 'w-3.5 h-3.5', text: 'text-[10px]' },
    md: { box: 'w-12 h-12', icon: 'w-6 h-6', text: 'text-xs' },
    lg: { box: 'w-20 h-20', icon: 'w-10 h-10', text: 'text-sm' },
    xl: { box: 'w-28 h-28', icon: 'w-14 h-14', text: 'text-base' },
  };
  const s = sizes[size];
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className={`${s.box} ${rank.bg} ${rank.border} ${rank.glow} ${animate ? 'animate-pulse' : ''} border-2 rounded-md flex items-center justify-center shadow-lg relative`}
        style={{ boxShadow: `0 0 24px var(--tw-shadow-color, rgba(0,0,0,0.5))` }}
      >
        <Icon className={`${s.icon} ${rank.color}`} />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_30%,rgba(255,255,255,0.08)_50%,transparent_70%)] rounded-md pointer-events-none" />
      </div>
      {showName && (
        <span className={`${s.text} ${rank.color} font-headline tracking-widest uppercase`}>
          {rank.name}
        </span>
      )}
    </div>
  );
};

const RankPreviewPage = () => {
  const [demoXp, setDemoXp] = useState(SAMPLE_USER.currentXp);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [leveledUpTo, setLeveledUpTo] = useState(null);

  const currentRank = getCurrentRank(demoXp);
  const nextRank = getNextRank(demoXp);
  const progressPct = nextRank
    ? ((demoXp - currentRank.minXp) / (nextRank.minXp - currentRank.minXp)) * 100
    : 100;
  const xpToNext = nextRank ? nextRank.minXp - demoXp : 0;

  const simulateXpGain = (amount) => {
    const newXp = demoXp + amount;
    const oldRank = getCurrentRank(demoXp);
    const newRank = getCurrentRank(newXp);
    setDemoXp(newXp);
    if (newRank.id !== oldRank.id) {
      setLeveledUpTo(newRank);
      setShowLevelUp(true);
    }
  };

  const resetDemo = () => {
    setDemoXp(SAMPLE_USER.currentXp);
    setShowLevelUp(false);
  };

  return (
    <div className="min-h-screen bg-black text-amber-50" data-testid="rank-preview-page">
      {/* Film grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04] z-50 mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' /%3E%3C/svg%3E")',
        }}
      />

      {/* Top nav */}
      <div className="border-b-2 border-amber-900/30 bg-gradient-to-b from-gray-950 to-black sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/feed"
            className="flex items-center gap-2 text-amber-500 hover:text-amber-400 font-headline tracking-wider text-sm"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO PRECINCT
          </Link>
          <Badge className="bg-amber-900/40 text-amber-400 border border-amber-700/50 font-typewriter">
            PREVIEW · NOT LIVE
          </Badge>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 pt-12 pb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-700" />
          <span className="text-amber-700 font-typewriter text-xs tracking-[0.3em]">
            CONFIDENTIAL · PERSONNEL FILE
          </span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-700" />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline tracking-tight text-red-500 mb-3">
          DETECTIVE RANKS
        </h1>
        <p className="text-amber-200/70 font-typewriter max-w-2xl">
          Every action in the precinct earns experience. Climb the ranks, unlock perks, and
          carve your name into the case files. Here&apos;s how it would look.
        </p>
      </div>

      {/* Profile card — current rank + progress */}
      <div className="max-w-7xl mx-auto px-4 mb-12">
        <Card className="bg-gradient-to-br from-gray-900 via-gray-950 to-black border-2 border-amber-900/50 overflow-hidden">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Left: rank badge */}
              <div className="lg:col-span-1 p-8 bg-gradient-to-br from-amber-900/10 to-transparent border-r-0 lg:border-r-2 border-b-2 lg:border-b-0 border-amber-900/30 flex flex-col items-center justify-center">
                <span className="font-typewriter text-amber-600/80 text-xs tracking-widest mb-4">
                  CURRENT RANK
                </span>
                <RankBadge rank={currentRank} size="xl" showName={false} />
                <h2 className={`text-3xl font-headline tracking-wider mt-4 ${currentRank.color}`}>
                  {currentRank.name.toUpperCase()}
                </h2>
                <p className="text-gray-400 font-typewriter text-sm text-center mt-2 italic">
                  &ldquo;{currentRank.tagline}&rdquo;
                </p>
              </div>

              {/* Right: progress + activity */}
              <div className="lg:col-span-2 p-8">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-typewriter text-amber-600/80 text-xs tracking-widest">
                    EXPERIENCE
                  </span>
                  <span className="font-headline text-amber-400 text-2xl">
                    {demoXp.toLocaleString()} XP
                  </span>
                </div>

                {/* Progress bar */}
                <div className="relative mb-2">
                  <div className="h-3 bg-gray-800 border border-amber-900/40 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-700 via-amber-500 to-red-500 transition-all duration-700"
                      style={{ width: `${Math.min(progressPct, 100)}%` }}
                    />
                  </div>
                  {/* Tick marks */}
                  <div className="flex justify-between mt-1 text-[10px] font-typewriter text-gray-500">
                    <span>{currentRank.minXp.toLocaleString()}</span>
                    <span>{nextRank ? nextRank.minXp.toLocaleString() : 'MAX'}</span>
                  </div>
                </div>

                {nextRank ? (
                  <p className="text-amber-200/70 font-typewriter text-sm mb-6">
                    <span className="text-amber-400 font-bold">{xpToNext.toLocaleString()} XP</span>{' '}
                    to <span className={nextRank.color}>{nextRank.name}</span>
                  </p>
                ) : (
                  <p className="text-red-400 font-typewriter text-sm mb-6">
                    Maximum rank achieved. Legend status.
                  </p>
                )}

                {/* Recent activity */}
                <div>
                  <h3 className="font-headline text-amber-600 text-sm tracking-widest mb-3">
                    RECENT XP
                  </h3>
                  <div className="space-y-2">
                    {SAMPLE_USER.recentActivity.map((a) => (
                      <div
                        key={a.what}
                        className="flex items-center justify-between border-b border-amber-900/20 pb-2 font-typewriter text-sm"
                      >
                        <span className="text-gray-300">{a.what}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-amber-500 font-bold">+{a.xp}</span>
                          <span className="text-gray-600 text-xs w-20 text-right">{a.when}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demo controls — simulate gaining XP */}
      <div className="max-w-7xl mx-auto px-4 mb-12">
        <div className="bg-amber-950/20 border-2 border-dashed border-amber-700/40 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-amber-500" />
            <h3 className="font-headline text-amber-400 tracking-widest text-sm">
              TRY IT — SIMULATE XP GAINS
            </h3>
          </div>
          <p className="text-amber-200/60 font-typewriter text-sm mb-4">
            Click the buttons to feel the progression. Watch the bar fill and trigger a level-up.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-amber-700 text-amber-400 hover:bg-amber-900/30"
              onClick={() => simulateXpGain(100)}
              data-testid="demo-add-100"
            >
              +100 XP
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-700 text-amber-400 hover:bg-amber-900/30"
              onClick={() => simulateXpGain(500)}
              data-testid="demo-add-500"
            >
              +500 XP
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-700 text-red-400 hover:bg-red-900/30"
              onClick={() => simulateXpGain(2000)}
              data-testid="demo-add-2000"
            >
              +2,000 XP (force level-up)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-400 hover:text-amber-400 ml-auto"
              onClick={resetDemo}
              data-testid="demo-reset"
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Rank ladder */}
      <div className="max-w-7xl mx-auto px-4 mb-16">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-2xl font-headline tracking-widest text-amber-400">THE LADDER</h2>
          <div className="h-px flex-1 bg-amber-900/40" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
          {RANKS.map((r, idx) => {
            const isCurrent = r.id === currentRank.id;
            const isUnlocked = demoXp >= r.minXp;
            return (
              <div
                key={r.id}
                className={`relative p-4 rounded-md border-2 transition-all ${
                  isCurrent
                    ? 'border-amber-500 bg-amber-950/40 scale-105 shadow-lg shadow-amber-500/30'
                    : isUnlocked
                      ? `${r.border} bg-gray-900/60 opacity-90`
                      : 'border-gray-800 bg-gray-950/40 opacity-50'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-black text-[10px] font-headline tracking-wider rounded-sm">
                    YOU
                  </div>
                )}
                <div className="flex flex-col items-center text-center">
                  <RankBadge rank={r} size="md" showName={false} />
                  <h4 className={`mt-3 font-headline text-sm tracking-widest ${r.color}`}>
                    {r.name.toUpperCase()}
                  </h4>
                  <span className="text-[10px] font-typewriter text-gray-500 mt-1">
                    {r.minXp.toLocaleString()}+ XP
                  </span>
                  <div className="mt-3 pt-3 border-t border-amber-900/20 w-full">
                    <ul className="text-[11px] font-typewriter text-gray-400 space-y-1 text-left">
                      {r.perks.map((p) => (
                        <li key={p} className="flex items-start gap-1">
                          <span className="text-amber-600 mt-0.5">·</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <span className="absolute top-1 right-1.5 text-[9px] font-typewriter text-gray-600">
                  TIER {idx + 1}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* How to earn XP */}
      <div className="max-w-7xl mx-auto px-4 mb-16">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-2xl font-headline tracking-widest text-amber-400">EARN XP</h2>
          <div className="h-px flex-1 bg-amber-900/40" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {XP_SOURCES.map((src) => {
            const Icon = src.icon;
            return (
              <div
                key={src.label}
                className="flex items-center gap-3 p-4 bg-gray-900/60 border border-amber-900/30 rounded-md hover:border-amber-700/60 transition-colors"
              >
                <div className="w-10 h-10 bg-amber-900/40 border border-amber-700/40 rounded flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-200 font-typewriter text-sm truncate">{src.label}</p>
                  <p className="text-amber-500 font-headline text-xs tracking-wider">
                    {src.amount}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat preview */}
      <div className="max-w-7xl mx-auto px-4 mb-16">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-2xl font-headline tracking-widest text-amber-400">IN CHAT</h2>
          <div className="h-px flex-1 bg-amber-900/40" />
        </div>
        <p className="text-amber-200/60 font-typewriter text-sm mb-4">
          Ranks appear next to every member&apos;s name in chat and DMs, so veterans get the
          recognition they earned.
        </p>
        <div className="bg-gray-950 border-2 border-amber-900/40 rounded-lg p-5 space-y-3 max-w-2xl">
          {[
            { rank: RANKS[6], name: 'BelialRune', msg: 'New case drops tomorrow. Lock your doors.', mine: true },
            { rank: RANKS[5], name: 'NightOwl_82', msg: "Captain confirms. This one's gonna be wild." },
            { rank: RANKS[3], name: 'EvidenceTag', msg: 'Counting the hours...' },
            { rank: RANKS[1], name: 'NewKidOnBlock', msg: 'first time here — what should I read first?' },
            { rank: RANKS[2], name: 'GumshoeGus', msg: 'Start with The Velvet Lounge Murders. Trust.' },
          ].map((m) => (
            <div key={m.name} className="flex items-start gap-3">
              <RankBadge rank={m.rank} size="sm" showName={false} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`font-headline text-sm tracking-wider ${
                      m.mine ? 'text-red-500' : m.rank.color
                    }`}
                  >
                    {m.name}
                  </span>
                  <span className={`text-[10px] font-typewriter ${m.rank.color} opacity-70`}>
                    {m.rank.name.toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-300 font-typewriter text-sm mt-0.5">{m.msg}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Level-up modal */}
      {showLevelUp && leveledUpTo && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowLevelUp(false)}
          data-testid="level-up-modal"
        >
          <div
            className="relative bg-gradient-to-br from-gray-900 to-black border-4 border-amber-500 rounded-lg max-w-md w-full p-8 shadow-2xl shadow-amber-500/40"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-red-500" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-red-500" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-red-500" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-red-500" />

            <button
              onClick={() => setShowLevelUp(false)}
              className="absolute top-3 right-3 text-amber-600 hover:text-amber-400"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <p className="font-typewriter text-red-500 text-xs tracking-[0.4em] mb-2 animate-pulse">
                · PROMOTION NOTICE ·
              </p>
              <h2 className="text-3xl font-headline tracking-wider text-amber-400 mb-6">
                YOU&apos;VE BEEN PROMOTED
              </h2>

              <div className="flex items-center justify-center mb-6">
                <RankBadge rank={leveledUpTo} size="xl" showName={false} animate />
              </div>

              <p className={`text-2xl font-headline tracking-widest mb-2 ${leveledUpTo.color}`}>
                {leveledUpTo.name.toUpperCase()}
              </p>
              <p className="text-gray-400 font-typewriter text-sm italic mb-6">
                &ldquo;{leveledUpTo.tagline}&rdquo;
              </p>

              <div className="bg-amber-950/30 border border-amber-700/40 rounded-md p-4 text-left mb-6">
                <p className="font-headline text-amber-600 text-xs tracking-widest mb-2">
                  NEW PERKS UNLOCKED
                </p>
                <ul className="space-y-1.5">
                  {leveledUpTo.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 font-typewriter text-sm text-amber-100">
                      <Star className="w-3 h-3 text-amber-500 mt-1 flex-shrink-0" fill="currentColor" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => setShowLevelUp(false)}
                className="bg-amber-600 hover:bg-amber-500 text-black font-headline tracking-wider w-full"
                data-testid="level-up-close"
              >
                CONTINUE THE INVESTIGATION
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 border-t-2 border-amber-900/30 mt-12">
        <p className="text-center text-gray-500 font-typewriter text-sm">
          This is a preview. Nothing here is wired to your live data yet.
          <br />
          Let Daria know which parts you want kept, changed, or scrapped.
        </p>
      </div>
    </div>
  );
};

export default RankPreviewPage;
